import type { ElementType, ReactNode } from "react";
import moment from "moment/moment";
import {
  IconAlertTriangle,
  IconCalendar,
  IconHome,
  IconPercentage,
  IconTrendingUp,
} from "@tabler/icons-react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import Text, { type Color } from "../UI/Text";
import Title from "../UI/Title";
import { Table } from "../UI/Table";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import type { RentabiliteBien } from "../../models/statsRentabilite";
import { warningMessage } from "./rentabiliteWarnings";
import styles from "./RentabiliteDetailDialog.module.css";

function moisLabel(mois: string): string {
  const label = moment(mois, "YYYY-MM").format("MMMM YYYY");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function pretExplication(bien: RentabiliteBien): string {
  if (bien.pret_source === "echeancier") {
    return `Mensualités issues de l'échéancier du prêt, dont ${currencyFormatter(
      bien.interets,
    )} d'intérêts et ${currencyFormatter(
      bien.capital_amorti,
    )} de capital amorti sur la période.`;
  }
  if (bien.pret_source === "balances") {
    return `Somme des écritures de type prêt affectées au bien (Prêt immobilier, Prêt (autre), intérêts d'emprunt, assurance de prêt)${
      bien.interets
        ? `, dont ${currencyFormatter(bien.interets)} d'intérêts`
        : ""
    } — aucun échéancier n'est rattaché.`;
  }
  return "Aucun prêt rattaché : aucune mensualité n'est déduite.";
}

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: ElementType;
  children: ReactNode;
}) {
  return (
    <div className={styles.sectionTitle}>
      <Icon size={20} stroke={1.5} className={styles.sectionIcon} />
      <Title variant="deca">{children}</Title>
    </div>
  );
}

interface LigneCalculProps {
  label: string;
  signe: "+" | "−" | "=";
  montant: string;
  explication?: string;
  isTotal?: boolean;
  color?: Color;
}

function LigneCalcul({
  label,
  signe,
  montant,
  explication,
  isTotal = false,
  color,
}: LigneCalculProps) {
  return (
    <div className={isTotal ? styles.ligneTotal : styles.ligne}>
      <div className={styles.ligneMontants}>
        <Text as="span" weight={isTotal ? "bold" : "medium"}>
          {label}
        </Text>
        <Text
          as="span"
          weight={isTotal ? "bold" : "medium"}
          color={color}
          className={styles.montant}
        >
          {signe} {montant}
        </Text>
      </div>
      {explication && <Text variant="deci">{explication}</Text>}
    </div>
  );
}

interface RentabiliteDetailDialogProps {
  bien: RentabiliteBien | null;
  nbMois: number;
  debut?: string;
  fin?: string;
  onOpenChange: (open: boolean) => void;
}

export default function RentabiliteDetailDialog({
  bien,
  nbMois,
  debut,
  fin,
  onOpenChange,
}: RentabiliteDetailDialogProps) {
  if (!bien) {
    return null;
  }

  const loyersAnnualises = (bien.loyers * 12) / nbMois;
  const depensesAnnualisees = (bien.depenses * 12) / nbMois;
  const moisAvecMouvement = bien.par_mois.filter(
    (m) => m.loyers !== 0 || m.depenses !== 0 || m.pret !== 0,
  );

  return (
    <ModalDialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      header={
        <>
          Détail du calcul —{" "}
          <span className={styles.dialogHeaderNom}>{bien.nom}</span>
        </>
      }
      subtitle={`Du ${moment(debut).format("DD/MM/YYYY")} au ${moment(
        fin,
      ).format("DD/MM/YYYY")} (${nbMois} mois)${
        bien.is_folder
          ? " · ligne immeuble : agrège les biens de l'immeuble"
          : ""
      }`}
    >
      <ModalDialogBody>
        <div className={styles.sections}>
          <section className={styles.section}>
            <SectionTitle icon={IconTrendingUp}>Cashflow</SectionTitle>
            <div className={styles.sheet}>
              <LigneCalcul
                label="Loyers encaissés"
                signe="+"
                montant={currencyFormatter(bien.loyers)}
                explication="Paiements reçus des locataires sur la période, plus les recettes bancaires rattachées au bien. Les appels de loyer non payés ne comptent pas."
              />
              <LigneCalcul
                label="Dépenses"
                signe="−"
                montant={currencyFormatter(bien.depenses)}
                explication="Écritures de charges et taxes affectées au bien. Les travaux n'en font pas partie (ils s'ajoutent au prix de revient), ni les remboursements de prêt (comptés dans les mensualités)."
              />
              <LigneCalcul
                label="Mensualités de prêt"
                signe="−"
                montant={currencyFormatter(bien.pret)}
                explication={pretExplication(bien)}
              />
              <LigneCalcul
                label="Cashflow"
                signe="="
                montant={`${currencyFormatter(
                  bien.cashflow,
                )} (soit ${currencyFormatter(bien.cashflow_mensuel)} / mois)`}
                isTotal
                color={bien.cashflow >= 0 ? "success" : "danger"}
              />
            </div>
            {bien.capital_restant_du !== null && (
              <Text variant="deci">
                Capital restant dû en fin de période :{" "}
                {currencyFormatter(bien.capital_restant_du)}.
              </Text>
            )}
          </section>

          <section className={styles.section}>
            <SectionTitle icon={IconPercentage}>Rentabilité</SectionTitle>
            {bien.prix_achat_total !== null ? (
              <div className={styles.sheet}>
                <LigneCalcul
                  label="Prix de revient"
                  signe="="
                  montant={currencyFormatter(bien.prix_achat_total)}
                  explication={`Prix d'achat renseigné sur le bien${
                    bien.frais_notaire_total
                      ? ` + ${currencyFormatter(
                          bien.frais_notaire_total,
                        )} de frais de notaire`
                      : ""
                  }${
                    bien.travaux_total
                      ? ` + ${currencyFormatter(bien.travaux_total)} de travaux`
                      : ""
                  }. Les travaux réalisés s'ajoutent au prix du bien, pas aux dépenses.`}
                />
                <LigneCalcul
                  label="Loyers annualisés"
                  signe="="
                  montant={currencyFormatter(loyersAnnualises)}
                  explication={`Loyers de la période ramenés à 12 mois : ${currencyFormatter(
                    bien.loyers,
                  )} × 12 ÷ ${nbMois}.`}
                />
                <LigneCalcul
                  label="Rentabilité brute"
                  signe="="
                  montant={`${bien.rentabilite_brute?.toLocaleString(
                    "fr-FR",
                  )} %`}
                  explication="Loyers annualisés ÷ prix de revient."
                  isTotal
                />
                <LigneCalcul
                  label="Rentabilité nette"
                  signe="="
                  montant={`${bien.rentabilite_nette?.toLocaleString(
                    "fr-FR",
                  )} %`}
                  explication={`(Loyers − dépenses) annualisés ÷ prix de revient, soit (${currencyFormatter(
                    loyersAnnualises,
                  )} − ${currencyFormatter(
                    depensesAnnualisees,
                  )}) ÷ ${currencyFormatter(bien.prix_achat_total)}.`}
                  isTotal
                />
              </div>
            ) : (
              <Text color="muted">
                Prix d'achat non renseigné : les rentabilités ne peuvent pas
                être calculées.
              </Text>
            )}
          </section>

          {bien.taux_occupation !== null && (
            <section className={styles.section}>
              <SectionTitle icon={IconHome}>Occupation</SectionTitle>
              <div className={styles.sheet}>
                <LigneCalcul
                  label="Taux d'occupation"
                  signe="="
                  montant={`${bien.taux_occupation.toLocaleString("fr-FR")} %`}
                  explication={
                    bien.is_folder
                      ? "Moyenne des taux d'occupation des biens de l'immeuble."
                      : "Part des jours de la période couverts par au moins un bail. La vacance n'est comptée qu'à partir de la date d'achat du bien, quand elle est renseignée."
                  }
                />
              </div>
            </section>
          )}

          {bien.warnings.length > 0 && (
            <section className={styles.section}>
              <SectionTitle icon={IconAlertTriangle}>
                Points d'attention
              </SectionTitle>
              <ul className={styles.warningList}>
                {bien.warnings.map((warning) => (
                  <li key={warning.code}>
                    <Text as="span" variant="deci">
                      {warningMessage(warning)}
                    </Text>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {moisAvecMouvement.length > 0 && (
            <section className={styles.section}>
              <SectionTitle icon={IconCalendar}>
                Détail mois par mois
              </SectionTitle>
              <Table>
                <Table.Header>
                  <Table.Column>Mois</Table.Column>
                  <Table.Column align="right">Loyers</Table.Column>
                  <Table.Column align="right">Dépenses</Table.Column>
                  <Table.Column align="right">Prêt</Table.Column>
                  <Table.Column align="right">Cashflow</Table.Column>
                </Table.Header>
                <Table.Body>
                  {moisAvecMouvement.map((m) => (
                    <Table.Row key={m.mois}>
                      <Table.Cell>{moisLabel(m.mois)}</Table.Cell>
                      <Table.Cell align="right" className={styles.montant}>
                        {currencyFormatter(m.loyers)}
                      </Table.Cell>
                      <Table.Cell align="right" className={styles.montant}>
                        {currencyFormatter(m.depenses)}
                      </Table.Cell>
                      <Table.Cell align="right" className={styles.montant}>
                        {currencyFormatter(m.pret)}
                      </Table.Cell>
                      <Table.Cell align="right" className={styles.montant}>
                        <Text
                          as="span"
                          color={m.cashflow >= 0 ? "success" : "danger"}
                          weight="medium"
                        >
                          {currencyFormatter(m.cashflow)}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              {moisAvecMouvement.length < bien.par_mois.length && (
                <Text variant="deci">
                  Les mois sans aucun mouvement ne sont pas affichés.
                </Text>
              )}
            </section>
          )}
        </div>
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Fermer
        </Button>
      </ModalDialogFooter>
    </ModalDialog>
  );
}
